import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IdentificationCard, House, UserCheck, CheckCircle, ArrowRight, ArrowLeft, Camera, Upload, ShieldCheck, Clock } from '@phosphor-icons/react';
import Navigation from '@/components/Navigation';
import { API } from '@/App';
import { toast } from 'sonner';

const STEPS = [
  { num: 1, title: 'Identity', icon: IdentificationCard },
  { num: 2, title: 'Address', icon: House },
  { num: 3, title: 'Background', icon: UserCheck }
];

const KYC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState({
    document_type: 'passport',
    document_number: '',
    full_legal_name: '',
    date_of_birth: '',
    address_line: '',
    city: '',
    country: '',
    document_url: '',
    address_proof_type: 'utility_bill',
    address_proof_url: '',
    background_consent: false
  });

  useEffect(() => {
    fetch(`${API}/kyc/my`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (d?.status) setExisting(d); })
      .catch(() => {});
  }, []);

  const update = (patch) => setForm({ ...form, ...patch });

  const submit = async () => {
    if (!form.background_consent) {
      toast.error('Please consent to background screening');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/kyc/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error('Submission failed');
      const data = await res.json();
      setExisting(data);
      toast.success('KYC submitted! Our team will review within 24-48h.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  // If already submitted, show status
  if (existing) {
    const statusMap = {
      under_review: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Under Review', desc: 'Our team is reviewing your documents. This usually takes 24-48 hours.' },
      verified: { color: 'bg-green-100 text-green-700', icon: ShieldCheck, label: 'Verified', desc: 'You\'re fully verified. You can now accept deliveries and unlock all platform features.' },
      rejected: { color: 'bg-red-100 text-red-700', icon: ShieldCheck, label: 'Rejected', desc: 'Your submission was not approved. Contact support for details.' }
    };
    const s = statusMap[existing.status] || statusMap.under_review;
    const Icon = s.icon;
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
            <div className={`w-20 h-20 rounded-full ${s.color} mx-auto flex items-center justify-center mb-6`}>
              <Icon size={36} weight="duotone" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="kyc-status-heading">
              Verification: {s.label}
            </h1>
            <p className="text-gray-600 max-w-md mx-auto mb-8">{s.desc}</p>
            <button onClick={() => navigate('/dashboard')} className="dropio-gradient-cta text-white font-semibold px-6 py-3 rounded-lg" data-testid="kyc-back-dashboard">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="kyc-title">
            Identity Verification
          </h1>
          <p className="text-gray-600 mt-1">Complete KYC to start accepting deliveries and earning</p>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6">
          <div className="flex items-center justify-between">
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              const isActive = step === s.num;
              const isDone = step > s.num;
              return (
                <div key={s.num} className="flex items-center flex-1">
                  <div className="flex items-center gap-3" data-testid={`kyc-step-${s.num}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                      isActive ? 'dropio-gradient text-white' : isDone ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {isDone ? <CheckCircle size={20} weight="fill" /> : <Icon size={18} weight="bold" />}
                    </div>
                    <div className="hidden md:block">
                      <p className={`text-xs font-bold uppercase tracking-wider ${isActive ? 'text-[#6D28D9]' : isDone ? 'text-green-600' : 'text-gray-400'}`}>Step {s.num}</p>
                      <p className={`text-sm font-semibold ${isActive || isDone ? 'text-gray-900' : 'text-gray-400'}`}>{s.title}</p>
                    </div>
                  </div>
                  {idx < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-3 ${step > s.num ? 'bg-green-300' : 'bg-gray-200'}`}></div>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-8">
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Verify your identity</h2>
              <p className="text-sm text-gray-500 mb-4">Upload one government-issued photo ID</p>

              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-2">Document type</label>
                <div className="grid grid-cols-3 gap-2">
                  {['passport', 'drivers_license', 'national_id'].map((t) => (
                    <button key={t} type="button" onClick={() => update({ document_type: t })}
                      className={`p-3 rounded-lg border-2 text-sm font-semibold capitalize ${form.document_type === t ? 'border-[#6D28D9] bg-purple-50 text-[#6D28D9]' : 'border-gray-200 text-gray-700'}`}
                      data-testid={`kyc-doctype-${t}`}>
                      {t.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>

              <Field label="Full legal name (as on ID)" value={form.full_legal_name} onChange={(v) => update({ full_legal_name: v })} testId="kyc-name" />
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Document number" value={form.document_number} onChange={(v) => update({ document_number: v })} testId="kyc-docnum" />
                <Field label="Date of birth" type="date" value={form.date_of_birth} onChange={(v) => update({ date_of_birth: v })} testId="kyc-dob" />
              </div>

              <FileUploadStub label="Upload front of ID" value={form.document_url} onChange={(v) => update({ document_url: v })} testId="kyc-doc-upload" />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Verify your address</h2>
              <p className="text-sm text-gray-500 mb-4">Provide proof of residence (issued within last 3 months)</p>

              <Field label="Street address" value={form.address_line} onChange={(v) => update({ address_line: v })} testId="kyc-address" />
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="City" value={form.city} onChange={(v) => update({ city: v })} testId="kyc-city" />
                <Field label="Country" value={form.country} onChange={(v) => update({ country: v })} testId="kyc-country" />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-2">Proof type</label>
                <div className="grid grid-cols-3 gap-2">
                  {[{ id: 'utility_bill', label: 'Utility Bill' }, { id: 'bank_statement', label: 'Bank Statement' }, { id: 'rental', label: 'Rental Agreement' }].map((t) => (
                    <button key={t.id} type="button" onClick={() => update({ address_proof_type: t.id })}
                      className={`p-3 rounded-lg border-2 text-sm font-semibold ${form.address_proof_type === t.id ? 'border-[#6D28D9] bg-purple-50 text-[#6D28D9]' : 'border-gray-200 text-gray-700'}`}
                      data-testid={`kyc-proof-${t.id}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <FileUploadStub label="Upload proof document" value={form.address_proof_url} onChange={(v) => update({ address_proof_url: v })} testId="kyc-proof-upload" />
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Background screening</h2>
              <p className="text-sm text-gray-500 mb-4">We screen against global fraud and watchlists. Your data is encrypted and never shared.</p>

              <div className="space-y-3">
                {[
                  { title: 'Identity verification', desc: 'Match name + DOB against ID database' },
                  { title: 'Sanctions & watchlist screening', desc: 'Global compliance check (OFAC, EU, UN)' },
                  { title: 'Fraud history check', desc: 'Cross-reference fraud reports across platforms' }
                ].map((c, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                    <CheckCircle size={20} weight="fill" className="text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{c.title}</p>
                      <p className="text-xs text-gray-500">{c.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <label className="flex items-start gap-2 text-sm cursor-pointer p-4 border border-gray-200 rounded-xl">
                <input type="checkbox" checked={form.background_consent} onChange={(e) => update({ background_consent: e.target.checked })}
                  className="mt-0.5 w-4 h-4 rounded text-[#6D28D9]" data-testid="kyc-consent" />
                <span className="text-gray-700">I consent to background screening and understand my information will be processed according to Dropio's Privacy Policy.</span>
              </label>
            </div>
          )}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
            <button onClick={() => setStep(Math.max(step - 1, 1))} disabled={step === 1}
              className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 disabled:opacity-50 flex items-center gap-2" data-testid="kyc-back">
              <ArrowLeft size={16} weight="bold" /> Back
            </button>
            {step < 3 ? (
              <button onClick={() => setStep(step + 1)} className="dropio-gradient-cta text-white font-semibold px-6 py-2.5 rounded-lg shadow-md flex items-center gap-2" data-testid="kyc-next">
                Continue <ArrowRight size={16} weight="bold" />
              </button>
            ) : (
              <button onClick={submit} disabled={loading}
                className="dropio-gradient-cta text-white font-semibold px-6 py-2.5 rounded-lg shadow-md disabled:opacity-60 flex items-center gap-2" data-testid="kyc-submit">
                {loading ? 'Submitting...' : 'Submit for Review'} <CheckCircle size={16} weight="bold" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, type = 'text', value, onChange, testId }) => (
  <div>
    <label className="text-sm font-semibold text-gray-700 block mb-1.5">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#6D28D9] focus:ring-2 focus:ring-purple-100"
      data-testid={testId} />
  </div>
);

const FileUploadStub = ({ label, value, onChange, testId }) => {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/upload`, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      onChange(data.path);
      toast.success('Document uploaded');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <label className="text-sm font-semibold text-gray-700 block mb-1.5">{label}</label>
      <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-all ${value ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-[#6D28D9] hover:bg-purple-50'}`}>
        {uploading ? (
          <div className="animate-spin h-8 w-8 rounded-full border-b-2 border-[#6D28D9]"></div>
        ) : value ? (
          <>
            <CheckCircle size={24} weight="fill" className="text-green-500 mb-2" />
            <p className="text-sm font-semibold text-green-700">Document uploaded</p>
            <p className="text-xs text-gray-500">Click to replace</p>
          </>
        ) : (
          <>
            <Upload size={24} weight="bold" className="text-gray-400 mb-2" />
            <p className="text-sm font-semibold text-gray-700">Click to upload</p>
            <p className="text-xs text-gray-500">PNG, JPG or PDF up to 5MB</p>
          </>
        )}
        <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFile} data-testid={testId} />
      </label>
    </div>
  );
};

export default KYC;
